import { useCallback } from 'react';

import { wolfswapApi } from '@/lib/api/clients/wolfswap';

import {
    requestLoginChallenge,
    submitLoginSignature,
} from '../services/walletAuth';
import { useWalletStore } from '../store';

import { useAuth } from './useAuth';
import { useWalletSigner } from './useWalletSigner';

/**
 * Legacy EOA sign-in: challenge, `personal_sign`, JWT.
 *
 * This only mints the application token; it grants no trading authority, so the
 * runtime stays in demo mode until a trading window is opened. Wallet-first
 * onboarding needs exactly that much — an authenticated identity for the voucher
 * and wallet-creation endpoints before any Snappy account exists.
 */
export function useEoaLogin() {
    const { isAuthenticated } = useAuth();
    const { signMessage } = useWalletSigner();
    const saveSession = useWalletStore((state) => state.saveSession);

    const login = useCallback(
        async (address: string) => {
            if (isAuthenticated) return;
            const challenge = await requestLoginChallenge(wolfswapApi, address);
            const signature = await signMessage(challenge.challenge);
            const session = await submitLoginSignature(
                wolfswapApi,
                address,
                challenge.token,
                signature,
            );
            // correlate the token with the address that actually signed the
            // challenge: it is the only record of which EOA this login belongs
            // to once the wallet moves on to another account.
            saveSession(session.token, session.expires, address);
        },
        [isAuthenticated, saveSession, signMessage],
    );

    return { login };
}
